import * as Dialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, X } from 'lucide-react'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { useEffect, useRef, useState } from 'react'
import { AmountStep } from '@/components/deposit/AmountStep'
import { MethodStep } from '@/components/deposit/MethodStep'
import { PaymentStep } from '@/components/deposit/PaymentStep'
import { DEPOSIT_STEP_ORDER, depositStepVariants } from '@/components/deposit/styles'
import { useDepositModal } from '@/context/DepositModalContext'
import { useDeposit } from '@/hooks/use-deposit'
import { cn } from '@/lib/utils'
import type { DepositStep } from '@/types/payment'

function getStepTitle(step: DepositStep, currencyLabel: string) {
	if (step === 'method') {
		return currencyLabel
	}

	return `Deposit ${currencyLabel}`
}

export function DepositModal() {
	const { isOpen, close } = useDepositModal()
	const {
		step,
		currency,
		currencyLabel,
		methodId,
		selectedMethod,
		availableMethods,
		amount,
		nowPaymentData,
		metamaskAddress,
		isBalancesLoading,
		isCurrenciesLoading,
		isSubmitting,
		canProceedFromMethod,
		canProceedFromAmount,
		createNowPaymentError,
		adminAddressError,
		selectMethod,
		updateAmount,
		goToAmountStep,
		goBack,
		submitDeposit,
		retrySubmit,
	} = useDeposit()

	const previousStepRef = useRef<DepositStep>(step)
	const [direction, setDirection] = useState(0)
	const isLoadingCurrency = isBalancesLoading || isCurrenciesLoading

	useEffect(() => {
		const previousStep = previousStepRef.current

		if (previousStep !== step) {
			setDirection(DEPOSIT_STEP_ORDER[step] > DEPOSIT_STEP_ORDER[previousStep] ? 1 : -1)
			previousStepRef.current = step
		}
	}, [step])

	const handleContinueFromMethod = () => {
		if (!canProceedFromMethod) {
			return
		}

		goToAmountStep()
	}

	const handleBack = () => {
		goBack()
	}

	const title = getStepTitle(step, currencyLabel)

	return (
		<Dialog.Root
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) close()
			}}
		>
			<Dialog.Portal>
				<Dialog.Overlay className='fixed inset-0 z-[1000] bg-black/70 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0' />
				<Dialog.Content
					className={cn(
						'fixed left-4 right-4 top-1/2 z-[1000] mx-auto flex max-h-[90vh] w-full max-w-[460px] -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[0_24px_80px_0_hsl(var(--background)/0.55)] outline-none',
						'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%]',
					)}
				>
					<div className='flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-6 sm:px-6 sm:py-7'>
						<div className='mb-6 flex items-center gap-3'>
							{step !== 'method' && (
								<button
									type='button'
									onClick={handleBack}
									className='flex h-10 w-10 items-center justify-center rounded-lg text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground'
									aria-label='Go back'
								>
									<ChevronLeft className='h-5 w-5' />
								</button>
							)}

							<div className='min-w-0 flex-1'>
								<Dialog.Title className='truncate text-xl font-semibold tracking-[-0.3px] text-foreground'>{title}</Dialog.Title>
								<Dialog.Description className='sr-only'>Deposit funds into your BetsCycle wallet</Dialog.Description>
							</div>

							<Dialog.Close
								className='flex h-10 w-10 items-center justify-center rounded-lg text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
								aria-label='Close'
							>
								<X className='h-5 w-5' />
							</Dialog.Close>
						</div>

						<div className='relative flex min-h-0 flex-1 flex-col overflow-hidden'>
							{isLoadingCurrency ? (
								<div className='flex flex-1 items-center justify-center'>
									<BetsCycleLoader size={24} />
								</div>
							) : !currency ? (
								<div className='flex flex-1 items-center justify-center text-center'>
									<p className='text-sm text-muted-foreground'>No deposit currency is available. Enable a balance first.</p>
								</div>
							) : (
								<AnimatePresence mode='wait' initial={false} custom={direction}>
									<motion.div
										key={step}
										custom={direction}
										variants={depositStepVariants}
										initial='enter'
										animate='center'
										exit='exit'
										transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
										className='flex min-h-0 flex-1 flex-col'
									>
										{step === 'method' ? (
											<MethodStep
												currencyLabel={currencyLabel}
												methods={availableMethods}
												selectedMethodId={methodId}
												onSelectMethod={selectMethod}
												onContinue={handleContinueFromMethod}
												canContinue={canProceedFromMethod}
											/>
										) : null}

										{step === 'amount' ? (
											<AmountStep
												currency={currency}
												currencyLabel={currencyLabel}
												amount={amount}
												onAmountChange={updateAmount}
												onSubmit={() => {
													void submitDeposit()
												}}
												canSubmit={canProceedFromAmount}
												isSubmitting={isSubmitting}
											/>
										) : null}

										{step === 'payment' && selectedMethod ? (
											<PaymentStep
												provider={selectedMethod.provider}
												currency={currency}
												currencyLabel={currencyLabel}
												amount={amount}
												nowPaymentData={nowPaymentData}
												metamaskAddress={metamaskAddress}
												onRetry={retrySubmit}
												hasSubmitError={Boolean(createNowPaymentError || adminAddressError)}
											/>
										) : null}
									</motion.div>
								</AnimatePresence>
							)}
						</div>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}
